import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  FormHelperText,
  Select,
} from "@mui/material";
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, parse } from "date-fns";
import acProcessService from "../../../../services/acProcessService";

const ACDates = () => {
  // Form state
  const [formData, setFormData] = useState({
    id: null,
    acType: "",
    dateFrom: null,
    dateTo: null,
  });

  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [acDates, setAcDates] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // AC Type options
  const acTypeOptions = [
    { value: "SEC", label: "SEC" },
    { value: "CAT", label: "CAT" },
    { value: "CAM", label: "CAM" },
    { value: "CAC", label: "CAC" },
    { value: "SAC", label: "SAC" },
    { value: "CAT (Integrated)", label: "CAT (Integrated)" },
    { value: "PTP", label: "PTP" },
    { value: "RMP", label: "RMP" },
    { value: "Review Committee", label: "Review Committee" },
  ];

  // Fetch all AC dates on component mount
  useEffect(() => {
    fetchAllACDates();
  }, []);

  // Fetch all AC dates (Mode 18)
  const fetchAllACDates = async () => {
    try {
      setLoading(true);
      const response = await acProcessService.getAllACDates();
      if (response.status === "success") {
        setAcDates(response.data || []);
        setError("");
      } else {
        setError("Failed to fetch AC dates");
        setAcDates([]);
      }
    } catch (err) {
      console.error("Error fetching AC dates:", err);
      setError(err.response?.message || "Error fetching AC dates");
      setAcDates([]);
    } finally {
      setLoading(false);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.acType) {
      errors.acType = "Please select AC Type";
    }

    if (!formData.dateFrom) {
      errors.dateFrom = "Please select Date From";
    }

    if (!formData.dateTo) {
      errors.dateTo = "Please select Date To";
    }

    if (formData.dateFrom && formData.dateTo) {
      if (formData.dateFrom > formData.dateTo) {
        errors.dateTo = "Date To must be greater than or equal to Date From";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check if AC date already exists (Mode 21)
  const alreadyExistingACDate = async () => {
    try {
      const dateFromStr = formData.dateFrom
        ? format(formData.dateFrom, "dd/MM/yyyy")
        : "";
      const dateToStr = formData.dateTo
        ? format(formData.dateTo, "dd/MM/yyyy")
        : "";

      const response = await acProcessService.checkExistingACDates({
        acType: formData.acType,
        date: dateFromStr,
        dateTo: dateToStr,
      });

      console.log("Check existing response:", response);

      if (
        response.status === "success" &&
        response.data &&
        response.data.length > 0
      ) {
        // Check if it's the same record being edited (use capital I for Id)
        if (isEditMode && response.data[0].Id === formData.id) {
          console.log("Same record being edited, not a duplicate");
          return false; // Not a duplicate, same record
        }
        console.log("Duplicate found");
        return true; // Duplicate found
      }
      console.log("No duplicate found");
      return false; // No duplicate
    } catch (err) {
      console.error("Error checking existing AC date:", err);
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      setError("Please fill all required fields correctly");
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Check for existing date before saving
      const exists = await alreadyExistingACDate();
      if (exists) {
        setError("AC Date already exists for this type and date range");
        setLoading(false);
        return;
      }

      const dateFromStr = format(formData.dateFrom, "dd/MM/yyyy");
      const dateToStr = format(formData.dateTo, "dd/MM/yyyy");

      let response;

      if (isEditMode) {
        // Mode 20: Update AC Date
        response = await acProcessService.updateACDate({
          id: formData.id,
          acType: formData.acType,
          date: dateFromStr,
          dateTo: dateToStr,
        });
      } else {
        // Mode 1: Insert AC Date
        response = await acProcessService.insertACDate({
          acType: formData.acType,
          date: dateFromStr,
          dateTo: dateToStr,
        });
      }

      if (response.status === "success") {
        setSuccessMessage(
          isEditMode
            ? "AC Date updated successfully"
            : "AC Date saved successfully",
        );
        resetForm();
        fetchAllACDates();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.message || "Failed to save AC Date");
      }
    } catch (err) {
      console.error("Error saving AC Date:", err);
      setError(err.response?.message || "Error saving AC Date");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = async (id) => {
    try {
      setLoading(true);
      // Mode 19: Get AC Date by ID
      const response = await acProcessService.getACDateById(id);

      if (response.status === "success" && response.data) {
        const dateData = response.data;

        // Parse dates from dd/MM/yyyy format
        const dateFrom = dateData.Date
          ? parse(dateData.Date, "dd/MM/yyyy", new Date())
          : null;
        const dateTo = dateData.DateTo
          ? parse(dateData.DateTo, "dd/MM/yyyy", new Date())
          : null;

        setFormData({
          id: dateData.Id,
          acType: dateData.AC_Type,
          dateFrom: dateFrom,
          dateTo: dateTo,
        });
        setIsEditMode(true);
        setError("");

        // Scroll to form
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setError("AC Date not found");
      }
    } catch (err) {
      console.error("Error fetching AC Date:", err);
      setError(err.response?.message || "Error loading AC Date");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete dialog open
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      // Mode 22: Deactivate AC Date
      const response = await acProcessService.deactivateACDate(deleteId);

      if (response.status === "success") {
        setSuccessMessage("AC Date deleted successfully");
        fetchAllACDates();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.message || "Failed to delete AC Date");
      }
    } catch (err) {
      console.error("Error deleting AC Date:", err);
      setError(err.response?.message || "Error deleting AC Date");
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      id: null,
      acType: "",
      dateFrom: null,
      dateTo: null,
    });
    setIsEditMode(false);
    setValidationErrors({});
    setError("");
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          {/* Header */}
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{ mb: 3, fontWeight: 600 }}
          >
            AC Dates
          </Typography>

          {/* Success Message */}
          {successMessage && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccessMessage("")}
            >
              {successMessage}
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* AC Type Dropdown */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!validationErrors.acType}>
                      <InputLabel>Type of AC *</InputLabel>
                      <Select
                        value={formData.acType}
                        onChange={(e) => handleChange("acType", e.target.value)}
                        label="Type of AC *"
                      >
                        <MenuItem value="">-----Select-----</MenuItem>
                        {acTypeOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {validationErrors.acType && (
                        <FormHelperText>
                          {validationErrors.acType}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Date From */}
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Date From *"
                      value={formData.dateFrom}
                      onChange={(newValue) =>
                        handleChange("dateFrom", newValue)
                      }
                      maxDate={formData.dateTo || undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!validationErrors.dateFrom,
                          helperText: validationErrors.dateFrom,
                        },
                      }}
                      format="dd/MM/yyyy"
                    />
                  </Grid>

                  {/* Date To */}
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Date To *"
                      value={formData.dateTo}
                      onChange={(newValue) => handleChange("dateTo", newValue)}
                      minDate={formData.dateFrom || undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!validationErrors.dateTo,
                          helperText: validationErrors.dateTo,
                        },
                      }}
                      format="dd/MM/yyyy"
                    />
                  </Grid>

                  {/* Form Actions */}
                  <Grid item xs={12}>
                    <Box
                      sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={
                          loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        disabled={loading}
                        sx={{
                          minWidth: 120,
                          backgroundColor: "#00024C",
                          "&:hover": {
                            backgroundColor: "#000139",
                          },
                        }}
                      >
                        {isEditMode ? "Update" : "Save"}
                      </Button>
                      {isEditMode && (
                        <Button
                          variant="outlined"
                          onClick={resetForm}
                          disabled={loading}
                          sx={{ minWidth: 120 }}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>

          {/* AC Dates Table */}
          {acDates.length > 0 && (
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    AC Dates List
                  </Typography>

                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchAllACDates}
                    disabled={loading}
                    sx={{ minWidth: 120 }}
                  >
                    Refresh
                  </Button>
                </Box>
                <TableContainer>
                  <Table sx={{ minWidth: 650, mt: 1 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Sr. No.
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          AC Type
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          From Date
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          To Date
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {acDates.map((row, index) => (
                        <TableRow
                          key={row.Id}
                          sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                        >
                          <TableCell align="center">{index + 1}</TableCell>
                          <TableCell align="center">{row.AC_Type}</TableCell>
                          <TableCell align="center">{row.Date}</TableCell>
                          <TableCell align="center">{row.DateTo}</TableCell>
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              <IconButton
                                color="primary"
                                onClick={() => handleEdit(row.Id)}
                                disabled={loading}
                                title="Edit"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteClick(row.Id)}
                                disabled={loading}
                                title="Delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* No data message */}
          {!loading && acDates.length === 0 && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No AC Dates found. Add a new AC Date to get started.
              </Typography>
            </Box>
          )}

          {/* Loading indicator */}
          {loading && acDates.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !loading && setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Do you want to delete this AC Date? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ACDates;
